import { EditorContent } from "@tiptap/react";
import { ComponentProps, useState } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { useIntl } from "react-intl";

import RichTextareaEditor from "@components/fields/RichTextareaEditor";
import RichTextareaProvider from "@components/fields/RichTextareaProvider";
import TextareaWrapper from "@components/fields/TextareaWrapper";
import useRichTextarea from "@hooks/useRichTextarea";
import { generateHTMLId } from "@utils/htmlUtils";

type Props<FormData extends FieldValues> = Omit<
  ComponentProps<typeof RichTextareaEditor>,
  "onChange" | "value" | "wrapperId"
> &
  Omit<ComponentProps<typeof RichTextareaProvider>, "editor"> &
  Pick<ComponentProps<typeof EditorContent>, "placeholder"> &
  Pick<ComponentProps<typeof TextareaWrapper>, "error" | "helperText" | "label" | "required"> & {
    autofocus?: boolean;
    form: UseFormReturn<FormData>;
    name: FieldPath<FormData>;
    preventEnter?: boolean;
  };

const RichTextarea = <FormData extends FieldValues>({
  autofocus = false,
  editorRefresher,
  error,
  form,
  helperText,
  id,
  journey,
  journeyId,
  label,
  name,
  onUploadsChange,
  placeholder,
  preventEnter = false,
  required,
  setTemplateId,
  uploadsWatch,
  ...richTextareaEditorProps
}: Props<FormData>) => {
  const intl = useIntl();

  const [textareaId] = useState<string>(id ?? generateHTMLId());

  const hasError = !!error && !!error.message;
  const register = form.control.register(name, { required });
  const watch = form.watch(name);

  const editor = useRichTextarea({
    autofocus,
    disabled: richTextareaEditorProps.disabled,
    onChange: (value: string) => {
      register.onChange({
        target: {
          name,
          value
        },
        type: "change"
      });
    },
    placeholder: placeholder ?? intl.formatMessage({ id: "dictionary.typeHere" }),
    preventEnter,
    value: watch
  });

  return (
    <RichTextareaProvider
      editor={editor}
      editorRefresher={editorRefresher}
      journey={journey}
      journeyId={journeyId}
      onUploadsChange={onUploadsChange}
      setTemplateId={setTemplateId}
      uploadsWatch={uploadsWatch}
    >
      <TextareaWrapper
        error={error}
        helperText={helperText}
        inputId={textareaId}
        label={label}
        required={required}
        setFocus={() => editor?.commands.focus("end")}
      >
        <RichTextareaEditor {...richTextareaEditorProps} hasError={hasError} id={textareaId} value={watch} />
      </TextareaWrapper>
    </RichTextareaProvider>
  );
};
export default RichTextarea;
